/**
 * TypeScript view of the `sokoban` format.
 * Source of truth: `shared/schema/sokoban.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;

export const WALL = "#";
export const FLOOR = " ";
export const GOAL = ".";
export const BOX = "$";
export const BOX_ON_GOAL = "*";
export const PLAYER = "@";
export const PLAYER_ON_GOAL = "+";

/** Every character a board row may hold, in the order the editor's palette shows them. */
export const BOARD_CHARS = [WALL, FLOOR, GOAL, BOX, BOX_ON_GOAL, PLAYER, PLAYER_ON_GOAL] as const;
export type BoardChar = (typeof BOARD_CHARS)[number];

export interface SokobanLimits {
  maxMoves?: number | null;
  maxPushes?: number | null;
}

export interface SokobanDefinition {
  puzzleType: "sokoban";
  typeVersion: number;
  /** One string per row, all the same length, in XSB notation. */
  board: string[];
  limits?: SokobanLimits;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface SokobanProgress {
  version: 1;
  /** The walkthrough so far, in LURD case. Replaying it from the board gives the position. */
  moves: string;
  /** The tail taken back, still redoable. Never sent to the server. */
  undone?: string;
}

export interface SokobanSubmission {
  moves: string;
}

/** The author's reference walkthrough, in the submission format. */
export type SokobanSolution = SokobanSubmission;

export const MAX_SIZE = 50;
export const MAX_MOVES = 10000;

export type Direction = "u" | "d" | "l" | "r";
export const DIRECTIONS: Direction[] = ["u", "d", "l", "r"];

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
